import React, { createContext, useContext, useEffect, useState } from "react";
import classNames from "classnames";
import { connect } from "react-redux";

const ThemeContext = createContext();
const ThemeUpdateContext = createContext();

export function useTheme() {
  return useContext(ThemeContext);
}

export function useThemeUpdate() {
  return useContext(ThemeUpdateContext);
}

const ThemeProvider = ({ lightVersion, ...props }) => {
  // Determine initial theme mode from localStorage or props
  const getInitialMode = () => {
    const savedMode = localStorage.getItem('themeMode');
    if (savedMode) {
      return savedMode === 'dark' ? 'dark' : 'light';
    }
    // If lightVersion is true from Redux, use light mode, otherwise use props.mode or default to dark
    if (lightVersion === true) {
      return 'light';
    }
    return props.mode || 'dark';
  };

  const [themeMode, setThemeMode] = useState(getInitialMode());
  const [theme, setTheme] = useState({
    main: "default", //other value can be passed "clean,shady,softy"
    sidebar: themeMode === "dark" ? "dark" : "light", //other value can be passed "light,white,theme"
    sidebarCompact: false,
    sidebarVisibility: false,
    sidebarMobile: false,
    header: themeMode === "dark" ? "dark" : "light", //other value can be passed "light,dark,theme"
    skin: themeMode, //other value can be passed "dark" or "light"
  });

  // Sync theme mode when Redux lightVersion changes
  useEffect(() => {
    const newMode = lightVersion === true ? 'light' : 'dark';
    if (newMode !== themeMode) {
      setThemeMode(newMode);
      localStorage.setItem('themeMode', newMode);
    }
  }, [lightVersion, themeMode]);

  // Update theme when themeMode changes
  useEffect(() => {
    setTheme(prevTheme => ({
      ...prevTheme,
      sidebar: themeMode === "dark" ? "dark" : "light",
      header: themeMode === "dark" ? "dark" : "light",
      skin: themeMode,
    }));
  }, [themeMode]);

  const themeUpdate = {
    uistyle: function (value) {
      setTheme({ ...theme, main: value });
    },
    sidebar: function (value) {
      setTheme({ ...theme, sidebar: value });
    },
    sidebarCompact: function (e) {
      setTheme({ ...theme, sidebarCompact: !theme.sidebarCompact });
    },
    sidebarVisibility: function (e) {
      setTheme({ ...theme, sidebarVisibility: !theme.sidebarVisibility });
    },
    sidebarHide: function (e) {
      setTheme({ ...theme, sidebarVisibility: false });
    },
    header: function (value) {
      setTheme({ ...theme, header: value });
    },
    skin: function (value) {
      setTheme({ ...theme, skin: value });
      const newMode = value === "dark" ? "dark" : "light";
      setThemeMode(newMode);
      localStorage.setItem('themeMode', newMode);
    },
    reset: function (e) {
      setTheme({ ...theme, main: theme.main, sidebar: theme.sidebar, header: theme.header, skin: theme.skin });
    },
  };

  const bodyClass = classNames({
    "nk-body bg-lighter npc-default has-sidebar no-touch nk-nio-theme": true,
  });

  useEffect(() => {
    const body = document.querySelector("body");
    body.className = bodyClass;
  }, []);

  useEffect(() => {
    const body = document.querySelector("body");
    if (theme.main === "default") {
      body.classList.add("ui-default");
      body.classList.remove("ui-clean", "ui-shady", "ui-softy");
    }
    if (theme.main === "clean") {
      body.classList.add(`ui-clean`);
      body.classList.remove("ui-default", "ui-shady", "ui-softy");
    }
    if (theme.main === "shady") {
      body.classList.add(`ui-shady`);
      body.classList.remove("ui-default", "ui-clean", "ui-softy");
    }
    if (theme.main === "softy") {
      body.classList.add(`ui-softy`);
      body.classList.remove("ui-default", "ui-clean", "ui-shady");
    }
    
    // Apply dark-mode class based on themeMode
    if (themeMode === "dark") {
      body.classList.add("dark-mode");
      body.classList.remove("light_version");
    } else {
      body.classList.remove("dark-mode");
      body.classList.add("light_version");
    }
    
    if (theme.sidebarVisibility === true) {
      body.classList.add("nav-shown");
    } else {
      body.classList.remove("nav-shown");
    }
  }, [theme, themeMode, bodyClass]);

  useEffect(() => {
  const body = document.querySelector("body");
  const observer = new ResizeObserver((entries) => {
    let width = entries[0].contentRect.width;
    if (width < 1200) {
        setTheme(prev => ({ ...prev, sidebarMobile: true }));
    } else {
        setTheme(prev => ({ ...prev, sidebarMobile: false, sidebarVisibility: false }));
    }
  });
  // observer.observe(body);
    
    return () => {
      // Cleanup if needed
    };
  }, []);

  return (
    <ThemeContext.Provider value={theme}>
      <ThemeUpdateContext.Provider value={themeUpdate}>{props.children}</ThemeUpdateContext.Provider>
    </ThemeContext.Provider>
  );
};

const mapStateToProps = state => ({
  lightVersion: state.settings?.lightVersion || false,
});

export default connect(mapStateToProps)(ThemeProvider);
